"use client";

import { useState, useMemo } from "react";
import { useTranslations, useMessages } from "next-intl";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faMinus,
} from "@awesome.me/kit-c10ef98ed3/icons/classic/regular";
import { IconButton } from "@mui/material";
import { useVariant } from "@/hooks/useVariant";

const disclaimerRichTextTags = {
  br: () => <br />,
};

export default function ReferencesSection() {
  const t = useTranslations("results-page.references-section");
  const [isReferencesOpen, setIsReferencesOpen] = useState(false);
  const variant = useVariant();

  const strings = useMessages() as Record<
    string,
    Record<string, Record<string, string | Record<string, string>>>
  >;
  const referenceSectionStrings =
    strings["results-page"]?.["references-section"] ?? {};

  const referencePrefix = referenceSectionStrings[variant]
    ? variant
    : "default";
  const referenceSource = (referenceSectionStrings[referencePrefix] ??
    {}) as Record<string, string>;

  // Parse references for any key beginning with "ref-" and sort numerically
  const referenceKeys = useMemo(
    () =>
      Object.keys(referenceSource)
        .filter((key) => key.startsWith("ref-"))
        .sort((a, b) => {
          const numA = parseInt(a.replace("ref-", ""), 10);
          const numB = parseInt(b.replace("ref-", ""), 10);
          return numA - numB;
        }),
    [referenceSource],
  );

  const handleToggle = () => {
    setIsReferencesOpen(!isReferencesOpen);
  };

  return (
    <section>
      <div className="references-section-content">
        <div className="references-section-disclaimer">
          <h2 className="references-section-heading">
            {t("disclaimer-title")}
          </h2>
          <div className="references-section-disclaimer-text">
            {t.rich("disclaimer-text", disclaimerRichTextTags)}
          </div>
        </div>

        <div className="references-section-references">
          <div className="references-section-references-header">
            <h2 className="references-section-heading">
              {t("references-title")}
            </h2>
            <IconButton
              className="references-section-toggle-button"
              onClick={handleToggle}
              aria-expanded={isReferencesOpen}
              aria-label={
                isReferencesOpen
                  ? t("collapse-references")
                  : t("expand-references")
              }
            >
              {isReferencesOpen ? (
                <FontAwesomeIcon icon={faMinus} aria-hidden="true" />
              ) : (
                <FontAwesomeIcon icon={faPlus} aria-hidden="true" />
              )}
            </IconButton>
          </div>
          {isReferencesOpen && referenceKeys.length > 0 && (
            <ol className="references-section-list">
              {referenceKeys.map((key) => (
                <li key={key}>{t(`${referencePrefix}.${key}`)}</li>
              ))}
            </ol>
          )}
        </div>
      </div>
    </section>
  );
}
